/**
 * Resource configuration for Employees Portfolio.
 *
 * Mirrors the structure of /src/pages/projects/portfolio/portfolio.config.ts.
 *
 * Notes on the current EmployeeSchema (Phoenix):
 * - No fields are flagged Facet=true, so every facet here uses `facetBy: false`
 *   (filtering works, but Typesense will not return count badges).
 * - The schema currently lacks Manager, License Type, Profile picture, and
 *   denormalized Name fields for LegalEntity / Department. Those filters and
 *   columns are deferred until the backend schema is updated.
 */

import type { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import i18n from 'i18next';

import { ConfigurationFeaturesResponse } from '@/api/REST/configuration-features';
import { TablePreferenceStorageKeys } from '@/hooks/grid/useTablePreferences';
import type { ResourceConfig } from '@/lib/resource-config';

export const employeesConfig = {
  pageIdentifier: 'EmployeePortfolioOverview',
  resourceKey: 'employees', // Matches i18n namespace
  typesenseCollection: 'employees',
  searchFields: ['Name', 'FirstName', 'LastName', 'Email', 'Initials'],
  facetSections: {
    employee: {
      get label() {
        return i18n.t('facetSections.Employee', { ns: 'employees' });
      },
    },
  },
  facets: {
    ['legal-entities']: {
      section: 'employee',
      get label() {
        return i18n.t('facets.LegalEntity', { ns: 'employees' });
      },
      field: 'LegalEntityId',
      type: 'int64',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'collection',
        collection: 'legalentities',
        idField: 'SourceId',
        slugField: 'SourceId',
        labelField: 'Name',
        sortField: 'Name',
      },
      facetBy: true,
      includeNullInNot: true,
      featureFlag: 'multiLegalEntities',
    },

    department: {
      section: 'employee',
      get label() {
        return i18n.t('facets.Department', { ns: 'employees' });
      },
      field: 'DepartmentId',
      facetField: 'DepartmentName', // readable counts via denormalized name
      type: 'int64',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'collection',
        collection: 'departments',
        idField: 'SourceId',
        slugField: 'SourceId',
        labelField: 'Name',
        sortField: 'Name',
      },
      facetBy: true,
      includeNullInNot: true,
      featureFlag: 'departments',
    },

    ['employee-type']: {
      section: 'employee',
      get label() {
        return i18n.t('facets.EmployeeType', { ns: 'employees' });
      },
      // Filter on RoleName directly — RoleId is not on the employee doc yet,
      // so the slug is the role name string (looked up from employeetypes).
      field: 'RoleName',
      type: 'string',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'collection',
        collection: 'employeetypes',
        idField: 'SourceId',
        slugField: 'Name',
        labelField: 'Name',
        sortField: 'Name',
      },
      facetBy: true,
      includeNullInNot: true,
    },

    manager: {
      section: 'employee',
      get label() {
        return i18n.t('facets.Manager', { ns: 'employees' });
      },
      field: 'ManagerId',
      facetField: 'ManagerName',
      type: 'int64',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'collection',
        collection: 'employees',
        idField: 'SourceId',
        slugField: 'SourceId',
        labelField: 'Name',
        sortField: 'Name',
        apiKeyName: 'managers',
      },
      facetBy: true,
      includeNullInNot: true,
    },

    status: {
      section: 'employee',
      get label() {
        return i18n.t('facets.Active', { ns: 'employees' });
      },
      field: 'Employed',
      type: 'bool',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'enum',
        values: ['true', 'false'],
      },
      facetBy: true,
      includeNullInNot: true,
    },

    // License type — mirrors the legacy /Employee/EmployeeOverview filter.
    // Gated by both the tenant-level multipleLicenseTypes feature (only
    // useful when the tenant actually has more than one license type in
    // use) and the NewEmployee page permission (matches the legacy
    // SecurityEngine.ValidatePageForUser check).
    ['license-type']: {
      section: 'employee',
      get label() {
        return i18n.t('facets.LicenseType', { ns: 'employees' });
      },
      field: 'LicenseTypeName',
      type: 'string',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'enum',
        // Matches the UserLicenseTypes enum names emitted by the Databricks
        // pipeline. NotSpecified is intentionally excluded — it's the
        // "unset" sentinel and isn't a meaningful filter choice.
        values: [
          'External',
          'Standard',
          'Advanced',
          'ExternalPlus',
          'TlpBasicLimited',
          'Limited',
          'TimeExpense',
        ],
      },
      facetBy: true,
      includeNullInNot: true,
      featureFlag: 'multipleLicenseTypes',
      requiredPagePermission: 'NewEmployee',
      // Only offer license-type choices that at least one employee actually
      // has — avoids zero-result picks for enum values not in use.
      hideEnumValuesWithoutMatches: true,
    },
  },
} satisfies ResourceConfig;

/**
 * Column sort field mappings (none yet for Employees).
 */
export const EMPLOYEES_SORT_FIELD_MAP: Record<string, string> = {};

/**
 * localStorage keys for Employees Portfolio preferences.
 */
export const EMPLOYEES_STORAGE_KEYS = {
  viewMode: 'employees-portfolio-view-mode',

  tableColumnVisibility: 'employees-portfolio-table-columns',
  tableColumnWidths: 'employees-portfolio-table-widths',
  tableColumnOrder: 'employees-portfolio-table-column-order',
  tableSort: 'employees-portfolio-table-sort',
  density: 'employees-portfolio-density',

  // Cards view keys reserved for future use
  cardFieldsVisibility: 'employees-portfolio-card-fields',
  cardsSort: 'employees-portfolio-cards-sort',
} satisfies TablePreferenceStorageKeys;

/**
 * Default column visibility for Employees table view.
 */
export const defaultTableColumnVisibility: GridColumnVisibilityModel = {
  profilePicture: true,
  Name: true,
  Position: true,
  Email: true,
  PrivatePhone: true,
  Mobile: true,
  DepartmentName: true,
  RoleName: true,
};

/**
 * Default field visibility for Employees cards view (cards view not built yet).
 */
export const defaultCardFieldsVisibility: GridColumnVisibilityModel = {
  Name: true,
  Position: true,
  Email: true,
  PrivatePhone: true,
  Mobile: true,
};

export interface ColumnVisibilityRequirement {
  /** Feature flag from configuration-features endpoint */
  featureFlag?: keyof ConfigurationFeaturesResponse;
}

export const columnVisibilityRequirements: Record<
  string,
  ColumnVisibilityRequirement
> = {};
